/* ============================================================
   LUVF product-photo tone normaliser - 19 Aug 2026 (v3).

   The catalogue is shot on three different backgrounds. Measured
   across all 230 first images:
     63  pure white          (lum >= 246)
     126 near-white          (236-245)
     18  light grey          (225-235)
     10  mid grey            (205-224)
     10  transparent PNG     (our own luvf.com cut-outs)
   A blanket multiply blend suits the first two groups and makes
   the grey ones muddy, which is why the grids looked inconsistent.

   WHAT IT DECIDES, PER PHOTO
     .lv-blend   near-white background  -> darken, melts into the paper
     .lv-matte   grey background        -> relight in canvas (v5), or
                                           painted tile as the fallback
     .lv-cutout  transparent PNG        -> leave alone, already clean
     .lv-fill    subject reaches the frame edge -> NO feather (see below)

   v3 adds .lv-fill: the feather in luvf-feather.css is a soft oval, right
   for a plate floating in a beige frame and wrong for photos that fill the
   rectangle. The probe samples the BORDER of each photo to tell them apart.

   Everything is cached in sessionStorage, so it costs one 48px thumbnail
   per photo per session and nothing after that.

   ------------------------------------------------------------
   v4 - 20 Aug 2026.
   1. BLEND_MIN 236 -> 226 (six to eight products sat a hair under the old
      cut-off and went matte while identical neighbours blended).
   2. .lv-clean: the blend mode changed from multiply to DARKEN. Any
      background at or above the paper lands EXACTLY on the paper, so for
      those photos there is no edge left to feather and the mask could only
      bite the plate. Those tiles skip the mask.
   3. The paper colour is MEASURED per tile (backdrop() walks to the first
      opaque ancestor) - --lm-bg was never declared anywhere and every tile
      fell back to #F2F1EB, which is wrong on the #FBF7F1 Dawn body.

   ------------------------------------------------------------
   v5 - 22 Aug 2026. RELIGHT. Anas: make the dark-background photos land
   on the page "in code and script as the photos might change here and
   there and new ones and new items" - not by editing files one by one.
   Flood-fill the background inward from the borders, blend it to the
   tile's measured paper colour, cache the finished JPEG per browser
   (Cache API), swap the img. New photos get it automatically.

   v5.1 - same day, after Anas's coconut-chips screenshot ("some of the
   images have a BLUR? NO BLUR NO FEATHER in actual item"). On pale
   products (coconut, popcorn, couscous) the fill crept INTO the subject
   through its own light pixels and shaved its edges - read as blur.
   Three defences, all measured on his screenshots:
     - the fill must stay near one of TWO border reference colours
       (top-wall band and table surface measured separately - one global
       band left the chocolate wall remnant, none at all ate the coconut)
     - EROSION GATE: if under 70% of the filled pixels are interior
       (3x3-surrounded), the fill went stringy inside product texture -
       throw it away, keep the sharp matte fallback. A pale product that
       cannot be separated safely is NEVER softened.
     - edge feather cut to a single 3x3 pass (~1px anti-alias, no visible
       softness), working canvas up to 1400px, JPEG quality 0.93.

   ------------------------------------------------------------
   v6 - 3 Sep 2026. Anas's Sides screenshots: white squares with the plate
   edges and shadows eaten. Cause: tiles that were never measured. Measuring
   only started when a tile scrolled into view, each measurement had a 7 s
   give-up timer, and a tile that timed out (thumbnails queue behind the
   big photos) was marked "skip" for the whole visit - drawn plain, but
   STILL under the feather mask (luvf-feather.css). Four changes, code only:
     1. every tile is measured as soon as the page loads, hidden tabs
        included - a 48px thumbnail each, nothing to wait for on scroll
     2. no give-up: a failed or slow measurement is retried with backoff
        (2 s, 4 s, 8 s ... up to 6 tries); nothing is ever marked skip
     3. luvf-feather.css no longer masks an unmeasured tile - a plain
        photo can only ever look like a plain photo
     4. near-white backgrounds (226-245, darker than the paper by a few
        levels) now also go through the relight pass, so they land on the
        paper exactly like pure-white ones. Relight is the only heavy step
        (a 1400px fetch per photo), so it runs when the tile comes near the
        viewport, two at a time - the classification itself never waits.
   ============================================================ */
(function () {
  var SEL = [
    '.lm-card .lm-img',
    '.card__inner .card__media',
    '.card-wrapper .card__media',
    '.product-media-container',
    '.lm-dish-photo',
    '.lm-dish-photo-sticky',
    '.luvf-star-media'
  ].join(',');

  var CACHE_KEY = 'luvf-tone-v3';
  var EDGE_TOLERANCE = 20;   /* how far a border pixel may drift from the corner colour */
  var FILL_THRESHOLD = 0.03; /* >3% of border pixels carrying subject = photo fills its frame */
  var BLEND_MIN = 226;       /* v4: was 236 - see the note in the header */
  var EDGE_ROOMY = 0.12;     /* subject only just touches the border band -> it can take the long fade */

  var PAPER_FALLBACK = [242, 241, 235];  /* #F2F1EB, the menu panel */
  var PAPER_SLACK = 1;                   /* one level of rounding slack on the probe */

  /* v5 relight tuning */
  var RL_MAX = 1400;         /* working-canvas long edge */
  var RL_CONT = 12;          /* per-channel smoothness the fill may cross */
  var RL_BAND = 30;          /* max distance from a border reference colour */
  var RL_LUM_FLOOR = 135;    /* never eat pixels darker than this */
  var RL_SAT_CAP = 62;       /* never eat strongly coloured pixels */
  var RL_MIN_FRAC = 0.30;    /* fill under 30% of frame = no background found, abort */
  var RL_MAX_FRAC = 0.92;    /* fill over 92% = ate the product, abort */
  var RL_INTERIOR = 0.70;    /* v5.1 erosion gate - see header */

  /* v6 */
  var MEASURE_TIMEOUT = 15000; /* was 7000; and a timeout no longer means skip */
  var RETRY_MAX = 6;           /* measurement retries, 2 s doubling, capped at 45 s */
  var RELIGHT_PAR = 2;         /* relight fetches in flight at once */

  function backdrop(el) {
    var n = el.parentElement, m;
    while (n && n !== document.documentElement) {
      m = /rgba?\(\s*(\d+)[\s,]+(\d+)[\s,]+(\d+)(?:[\s,\/]+([\d.]+))?/.exec(getComputedStyle(n).backgroundColor);
      if (m && (m[4] === undefined || parseFloat(m[4]) >= 0.99)) return [+m[1], +m[2], +m[3]];
      n = n.parentElement;
    }
    return PAPER_FALLBACK;
  }

  var cache = {};
  try { cache = JSON.parse(sessionStorage.getItem(CACHE_KEY) || '{}'); } catch (e) { cache = {}; }

  var writePending = false;
  function persist() {
    if (writePending) return;
    writePending = true;
    setTimeout(function () {
      writePending = false;
      try { sessionStorage.setItem(CACHE_KEY, JSON.stringify(cache)); } catch (e) {}
    }, 800);
  }

  var canvas = null, ctx = null;
  function measure(src) {
    return new Promise(function (resolve) {
      if (cache[src]) return resolve(cache[src]);
      if (!canvas) {
        canvas = document.createElement('canvas');
        canvas.width = 48; canvas.height = 48;
        ctx = canvas.getContext('2d', { willReadFrequently: true });
      }
      var probe = new Image();
      probe.crossOrigin = 'anonymous';
      var done = false;
      var finish = function (val) {
        if (done) return;
        done = true;
        if (val) { cache[src] = val; persist(); }
        resolve(val);
      };
      probe.onload = function () {
        try {
          ctx.clearRect(0, 0, 48, 48);
          ctx.drawImage(probe, 0, 0, 48, 48);
          var img = ctx.getImageData(0, 0, 48, 48).data;
          var at = function (x, y) { var i = (y * 48 + x) * 4; return [img[i], img[i+1], img[i+2], img[i+3]]; };

          var pts = [[1,1],[46,1],[1,46],[46,46],[24,1]];
          var r = 0, g = 0, b = 0, a = 0;
          for (var i = 0; i < pts.length; i++) {
            var d = at(pts[i][0], pts[i][1]);
            r += d[0]; g += d[1]; b += d[2]; a += d[3];
          }
          var n = pts.length;
          r = Math.round(r/n); g = Math.round(g/n); b = Math.round(b/n); a = Math.round(a/n);
          var lum = Math.round(0.2126*r + 0.7152*g + 0.0722*b);

          var band = 6, total = 0, off = 0;
          for (var y = 0; y < 48; y++) {
            for (var x = 0; x < 48; x++) {
              var inBand = (x < band || x > 47 - band || y < band || y > 47 - band);
              if (!inBand) continue;
              var p = at(x, y);
              total++;
              if (p[3] < 40) continue;
              var pl = 0.2126*p[0] + 0.7152*p[1] + 0.0722*p[2];
              if (Math.abs(pl - lum) > EDGE_TOLERANCE) off++;
            }
          }
          var edgeFrac = total ? off / total : 0;

          finish({ r: r, g: g, b: b, a: a, lum: lum, edge: Math.round(edgeFrac * 1000) / 1000 });
        } catch (e) { finish(null); }
      };
      probe.onerror = function () { finish(null); };
      setTimeout(function () { finish(null); }, MEASURE_TIMEOUT);
      probe.src = src.split('?')[0] + '?width=48';
    });
  }

  /* ---------- v5: the relight pass ---------- */

  var rlCache = undefined;
  function rlOpen() {
    if (rlCache !== undefined) return rlCache;
    try { rlCache = ('caches' in window) ? caches.open('luvf-relight-v2') : Promise.resolve(null); }
    catch (e) { rlCache = Promise.resolve(null); }
    return rlCache;
  }

  function lift(bm, paper) {
    var scale = Math.min(1, RL_MAX / Math.max(bm.width, bm.height));
    var W = Math.round(bm.width * scale), H = Math.round(bm.height * scale);
    var c = document.createElement('canvas'); c.width = W; c.height = H;
    var g = c.getContext('2d', { willReadFrequently: true });
    g.drawImage(bm, 0, 0, W, H);
    var idata;
    try { idata = g.getImageData(0, 0, W, H); } catch (e) { return null; }
    var d = idata.data, N = W * H;
    function lum(i4) { return 0.2126*d[i4] + 0.7152*d[i4+1] + 0.0722*d[i4+2]; }
    function sat(i4) {
      var mx = Math.max(d[i4], d[i4+1], d[i4+2]), mn = Math.min(d[i4], d[i4+1], d[i4+2]);
      return mx - mn;
    }
    /* two border reference colours: median of the TOP border (a wall or
       backdrop band) and median of the other three sides (the surface) */
    function median(arr) { arr.sort(function (a, b) { return a - b; }); return arr[arr.length >> 1] || 0; }
    var tr = [], tg = [], tb = [], sr = [], sg = [], sb = [];
    for (var x0 = 0; x0 < W; x0 += 3) {
      var t4 = x0 * 4; tr.push(d[t4]); tg.push(d[t4+1]); tb.push(d[t4+2]);
      var b4 = ((H - 1) * W + x0) * 4; sr.push(d[b4]); sg.push(d[b4+1]); sb.push(d[b4+2]);
    }
    for (var y0 = 0; y0 < H; y0 += 3) {
      var l4 = (y0 * W) * 4; sr.push(d[l4]); sg.push(d[l4+1]); sb.push(d[l4+2]);
      var r4 = (y0 * W + W - 1) * 4; sr.push(d[r4]); sg.push(d[r4+1]); sb.push(d[r4+2]);
    }
    var refs = [
      [median(tr), median(tg), median(tb)],
      [median(sr), median(sg), median(sb)]
    ];
    function nearRef(i4) {
      for (var q = 0; q < refs.length; q++) {
        var f = refs[q];
        if (Math.abs(d[i4] - f[0]) < RL_BAND &&
            Math.abs(d[i4+1] - f[1]) < RL_BAND &&
            Math.abs(d[i4+2] - f[2]) < RL_BAND) return true;
      }
      return false;
    }
    var mask = new Uint8Array(N);
    var stack = [];
    function ok(i4) { return d[i4+3] > 10 && lum(i4) > RL_LUM_FLOOR && sat(i4) < RL_SAT_CAP && nearRef(i4); }
    function seed(i) { if (!mask[i] && ok(i * 4)) { mask[i] = 1; stack.push(i); } }
    for (var x = 0; x < W; x++) { seed(x); seed((H - 1) * W + x); }
    for (var y = 0; y < H; y++) { seed(y * W); seed(y * W + W - 1); }
    while (stack.length) {
      var i = stack.pop(), px = i % W, py = (i / W) | 0, i4 = i * 4;
      var nbs = [];
      if (px > 0) nbs.push(i - 1);
      if (px < W - 1) nbs.push(i + 1);
      if (py > 0) nbs.push(i - W);
      if (py < H - 1) nbs.push(i + W);
      for (var k = 0; k < nbs.length; k++) {
        var j = nbs[k];
        if (mask[j]) continue;
        var j4 = j * 4;
        if (Math.abs(d[j4] - d[i4]) < RL_CONT &&
            Math.abs(d[j4+1] - d[i4+1]) < RL_CONT &&
            Math.abs(d[j4+2] - d[i4+2]) < RL_CONT &&
            ok(j4)) {
          mask[j] = 1; stack.push(j);
        }
      }
    }
    var covered = 0;
    for (var m = 0; m < N; m++) covered += mask[m];
    var frac = covered / N;
    if (frac < RL_MIN_FRAC || frac > RL_MAX_FRAC) return null;

    /* v5.1 EROSION GATE: a healthy background fill is solid - almost every
       filled pixel is surrounded by filled pixels. A fill that crept into
       product texture is stringy. Measure interior share, abort if low. */
    var interior = 0;
    for (var yy1 = 1; yy1 < H - 1; yy1++) {
      for (var xx1 = 1; xx1 < W - 1; xx1++) {
        var ii = yy1 * W + xx1;
        if (!mask[ii]) continue;
        if (mask[ii-1] && mask[ii+1] && mask[ii-W] && mask[ii+W] &&
            mask[ii-W-1] && mask[ii-W+1] && mask[ii+W-1] && mask[ii+W+1]) interior++;
      }
    }
    if (interior / covered < RL_INTERIOR) return null;

    /* single 3x3 pass = ~1px anti-alias on the seam. NOT a feather - the
       product itself is untouched and stays sharp. */
    var alpha = new Float32Array(N);
    for (var a0 = 0; a0 < N; a0++) alpha[a0] = mask[a0];
    var out = new Float32Array(N);
    for (var yb = 0; yb < H; yb++) {
      for (var xb = 0; xb < W; xb++) {
        var s = 0, n2 = 0;
        for (var dy = -1; dy <= 1; dy++) {
          var y3 = yb + dy; if (y3 < 0 || y3 >= H) continue;
          for (var dx = -1; dx <= 1; dx++) {
            var x3 = xb + dx; if (x3 < 0 || x3 >= W) continue;
            s += alpha[y3 * W + x3]; n2++;
          }
        }
        out[yb * W + xb] = s / n2;
      }
    }
    alpha = out;
    for (var p2 = 0; p2 < N; p2++) {
      var a2 = alpha[p2];
      if (a2 <= 0) continue;
      var p4 = p2 * 4;
      d[p4]   = Math.round(d[p4]   * (1 - a2) + paper[0] * a2);
      d[p4+1] = Math.round(d[p4+1] * (1 - a2) + paper[1] * a2);
      d[p4+2] = Math.round(d[p4+2] * (1 - a2) + paper[2] * a2);
    }
    g.putImageData(idata, 0, 0);
    return c;
  }

  function relight(tile, img, done) {
    var finished = false;
    var end = function () { if (finished) return; finished = true; if (done) done(); };
    var raw = (img.currentSrc || img.src || '').split('?')[0];
    if (!raw || raw.indexOf('data:') === 0 || raw.indexOf('blob:') === 0) { end(); return; }
    var key = raw + '?luvf-relight=2';
    var paper = backdrop(tile);
    var swap = function (url) {
      img.removeAttribute('srcset');
      img.removeAttribute('sizes');
      img.src = url;
      tile.classList.remove('lv-matte');
      tile.classList.add('lv-blend', 'lv-clean');
      tile.dataset.luvfTone = 'relight';
      end();
    };
    rlOpen().then(function (store) {
      var hit = store ? store.match(key) : Promise.resolve(null);
      Promise.resolve(hit).then(function (res) {
        if (res) { res.blob().then(function (b) { swap(URL.createObjectURL(b)); }).catch(end); return; }
        fetch(raw + '?width=' + RL_MAX, { mode: 'cors' })
          .then(function (r) { if (!r.ok) throw 0; return r.blob(); })
          .then(function (b) { return createImageBitmap(b); })
          .then(function (bm) {
            var c = lift(bm, paper);
            bm.close();
            if (!c) { end(); return; } /* photo this pass cannot handle safely: sharp matte fallback stays */
            c.toBlob(function (blob) {
              if (!blob) { end(); return; }
              swap(URL.createObjectURL(blob));
              rlOpen().then(function (s2) {
                if (s2) { try { s2.put(key, new Response(blob, { headers: { 'Content-Type': 'image/jpeg' } })); } catch (e) {} }
              });
            }, 'image/jpeg', 0.93);
          })
          .catch(end);
      }).catch(end);
    }).catch(end);
  }

  /* v6: relight scheduling - the heavy step waits for the viewport and runs two at a time */
  var rlQueue = [], rlActive = 0;
  function pump() {
    while (rlActive < RELIGHT_PAR && rlQueue.length) {
      var job = rlQueue.shift();
      rlActive++;
      relight(job.tile, job.img, function () { rlActive--; pump(); });
    }
  }
  var rio = ('IntersectionObserver' in window)
    ? new IntersectionObserver(function (entries) {
        entries.forEach(function (en) {
          if (!en.isIntersecting) return;
          rio.unobserve(en.target);
          var im = en.target.querySelector('img');
          if (im) { rlQueue.push({ tile: en.target, img: im }); pump(); }
        });
      }, { rootMargin: '600px 0px' })
    : null;
  function queueRelight(tile, img) {
    if (tile.dataset.luvfRelight) return;
    tile.dataset.luvfRelight = 'wait';
    if (rio) rio.observe(tile); else { rlQueue.push({ tile: tile, img: img }); pump(); }
  }

  function apply(tile) {
    if (tile.dataset.luvfTone) return;
    var img = tile.querySelector('img');
    if (!img) return;
    var src = img.currentSrc || img.src;
    if (!src || src.indexOf('data:') === 0) return;
    tile.dataset.luvfTone = 'pending';

    measure(src).then(function (t) {
      if (!t) {
        /* v6: never skip. Try again with backoff. */
        var tries = (+tile.dataset.luvfTry || 0) + 1;
        tile.dataset.luvfTry = tries;
        delete tile.dataset.luvfTone;
        if (tries <= RETRY_MAX) setTimeout(function () { apply(tile); }, Math.min(45000, 2000 * Math.pow(2, tries - 1)));
        return;
      }

      var paper = backdrop(tile);
      tile.style.setProperty('--lm-bg', 'rgb(' + paper[0] + ',' + paper[1] + ',' + paper[2] + ')');

      if (t.edge > FILL_THRESHOLD) {
        tile.classList.add('lv-fill');
        tile.dataset.luvfEdge = t.edge;
        tile.style.setProperty('--lvf-edge', t.edge <= EDGE_ROOMY ? 'min(12%, 26px)' : 'min(4%, 10px)');
      }

      if (t.a < 40) {
        tile.classList.add('lv-cutout');
        tile.dataset.luvfTone = 'cutout';
        return;
      }
      if (t.lum >= BLEND_MIN) {
        tile.classList.add('lv-blend');
        tile.dataset.luvfTone = 'blend';
        if (t.r >= paper[0] - PAPER_SLACK &&
            t.g >= paper[1] - PAPER_SLACK &&
            t.b >= paper[2] - PAPER_SLACK) {
          tile.classList.add('lv-clean');
        } else {
          /* v6: near-white but a few levels under the paper - darken leaves a
             faint square. Relight lands it on the paper exactly. */
          queueRelight(tile, img);
        }
      } else {
        /* darker than the paper: no blend can melt this. Paint the tile as
           the immediate fallback, then try the v5 relight. */
        tile.style.setProperty('--lv-matte', 'rgb(' + t.r + ',' + t.g + ',' + t.b + ')');
        tile.classList.add('lv-matte');
        tile.dataset.luvfTone = 'matte';
        queueRelight(tile, img);
      }
    });
  }

  /* v6: measure every tile straight away, hidden tabs included. The probe is a
     48px thumbnail, so the whole menu costs less than one product photo. The
     old IntersectionObserver gate is gone from measurement; only the relight
     pass still waits for the viewport (see queueRelight). */
  function scan(root) {
    (root || document).querySelectorAll(SEL).forEach(function (tile) {
      if (tile.dataset.luvfTone) return;
      apply(tile);
    });
  }

  function boot() { scan(document); }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else { boot(); }

  document.addEventListener('shopify:section:load', function (e) { scan(e.target); });
  /* menu page swaps panels without a reload */
  document.addEventListener('click', function () { setTimeout(function () { scan(document); }, 60); }, true);
})();
